import { chooseMFromNShuffle, random } from "seneca-common/utils/functions";
import pickRandom from "seneca-common/utils/functions/pickRandom";

import { GenerateContentOptions } from "../../types";
import { TestingContentType } from "../consts";
import { Block, Content, ContentSource } from "../types";
import { TESTABLE_WORD_REGEX } from "./consts";
import {
  canBeMultipleChoiceTested,
  canBeWordfillTested,
  getBlocks,
  getWordfillWords
} from "./utils";

const MAX_WRONG_OPTIONS = 2;

export function generateContent(
  source: ContentSource,
  { testingActive }: GenerateContentOptions
): Content {
  const {
    wrongAnswers,
    wordfillSentence,
    wordsOnly,
    shortEquation,
    longEquation
  } = source;

  const blocks = getBlocks(source);
  const shortEquationFormatted =
    !wordsOnly && formatEquation(shortEquation, blocks, true);
  const longEquationFormatted =
    longEquation && formatEquation(longEquation, blocks, false);
  const wordfillWords = getWordfillWords(blocks);
  const wordFillData = generateWordfillData(
    blocks,
    wordfillWords,
    wordfillSentence
  );
  if (!testingActive)
    return {
      ...source,
      equation: wordsOnly ? longEquationFormatted : shortEquationFormatted,
      ...wordFillData
    } as Content;

  const testedBlocks = blocks.filter(block => block.tested);
  const testingTypes: TestingContentType[] = [];

  if (canBeMultipleChoiceTested(source)) {
    testingTypes.push(TestingContentType.MULTIPLE_CHOICE);
  }

  if (canBeWordfillTested(source)) {
    testingTypes.push(TestingContentType.WORDFILL);
  }

  const testingType = pickRandom(testingTypes);

  switch (testingType) {
    case TestingContentType.MULTIPLE_CHOICE:
      const multiChoiceData = generateMultipleChoiceData(
        testedBlocks,
        wrongAnswers!
      );
      return {
        ...source,
        testingType,
        equation: longEquationFormatted,
        equationWithGap: formatEquation(
          longEquation,
          blocks,
          false,
          multiChoiceData.answers[multiChoiceData.correctAnswerIndex]
        ),
        ...multiChoiceData
      };

    case TestingContentType.WORDFILL:
    default:
      return {
        ...source,
        testingType,
        equation: shortEquationFormatted as string,
        ...wordFillData
      };
  }
}

export const compareFunction = (a: string, b: string) => {
  if (b.match(/\\/) && a.match(/\\/)) {
    return b.length - a.length;
  } else if (b.match(/\\/)) {
    return 1;
  } else if (a.match(/\\/)) {
    return -1;
  }

  return b.length - a.length;
};

export function formatEquation(
  equation: string,
  blocks: Array<Block>,
  shortForm: boolean,
  correctAnswer: string = ""
): string {
  let out = "";
  const placeholderCharacter = "҉";
  const searchSymbols = blocks.map(block => block.latexSymbol);
  const searchWords = blocks.map(block => block.word);
  const searchTerms = searchSymbols.concat(searchWords).sort(compareFunction);
  let toProcess = equation;
  const array = new Array(toProcess.length).fill("");
  let backslash = true;

  while (searchTerms.length) {
    const term = searchTerms.shift();

    if (!term!.includes("\\") && backslash) {
      backslash = false;
      let match: RegExpMatchArray | boolean | null = true;

      while (match) {
        // strip out words that start with \
        match = toProcess.match(/(\\[\w]+)[{}]/);

        if (match) {
          array[match.index!] = match[1];
          toProcess =
            toProcess.slice(0, match.index) +
            placeholderCharacter.repeat(match[1].length) +
            toProcess.slice(match.index! + match[1].length);
        }
      }
    }

    const isWord = searchWords.includes(term!);
    const color = blocks.filter(
      block => (isWord && block.word === term) || block.latexSymbol === term
    )[0].color;
    let index = 0;

    while (index !== -1) {
      index = toProcess.indexOf(term!);

      if (index !== -1) {
        const wordOrUnderscores =
          correctAnswer === term ? "\\_".repeat(correctAnswer.length) : term;
        array[index] = isWord
          ? `\\text{\\textcolor{${color}}{${wordOrUnderscores}}}`
          : `{\\color{${color}}${term}}`;
        toProcess =
          toProcess.slice(0, index) +
          placeholderCharacter.repeat(term!.length) +
          toProcess.slice(index + term!.length);
      }
    }
  }

  for (let i = 0; i < toProcess.length; i++) {
    if (toProcess.charAt(i) !== placeholderCharacter) {
      array[i] = toProcess.charAt(i);
    }

    out = array.join("");
  }

  return shortForm ? `\\Large{${out}}` : out;
}

export function generateWordfillData(
  blocks: Array<Block>,
  wordfillWords: Array<string>,
  sentence?: string
): Record<string, any> {
  const allSymbols = blocks.map(block => block.word);

  const testedSymbols = blocks
    .filter(block => block.tested)
    .map(block => block.word);

  const words: any[] = [];
  const wordStyles = {} as any;
  let toProcess: string | null | undefined = sentence;

  while (toProcess) {
    const testableWordMatch = toProcess.match(TESTABLE_WORD_REGEX);

    if (testableWordMatch) {
      const [match, allWordsString] = testableWordMatch;
      const { index } = testableWordMatch;
      words.push(toProcess.substring(0, index));
      toProcess = toProcess.substring(index! + match.length);
      const allWords = allWordsString.split("/");
      const otherPermittedWords = allWords.slice(1);
      const word = allWords[0];

      if (allSymbols.includes(word)) {
        words.push({
          word,
          otherPermittedWords,
          testing: wordfillWords.includes(word) && testedSymbols.includes(word)
        });
        const block = blocks.filter(block => block.word === word)[0];
        wordStyles[block.word] = {
          all: {
            color: block.color,
            borderColor: block.color
          }
        };
      } else {
        words.push(word);
      }
    } else {
      if (toProcess.length > 0) {
        words.push(toProcess);
      }

      toProcess = null;
    }
  }

  return {
    words: words,
    wordStyles: wordStyles
  };
}

export function generateMultipleChoiceData(
  blocks: Array<Block>,
  wrongAnswers: Array<string>
): Record<string, any> {
  const correctAnswer = blocks[random(0, blocks.length - 1)].word;
  const wrongAnswersLength = Math.min(MAX_WRONG_OPTIONS, wrongAnswers.length);
  const wrongAnswerIndexes = chooseMFromNShuffle(
    wrongAnswersLength,
    wrongAnswers.length
  );
  const correctAnswerIndex = random(0, wrongAnswersLength);
  let answers = wrongAnswerIndexes.map(index => wrongAnswers[index]);
  return {
    correctAnswer,
    correctAnswerIndex,
    answers: [
      ...answers.slice(0, correctAnswerIndex),
      correctAnswer,
      ...answers.slice(correctAnswerIndex)
    ]
  };
}
